const USER_TYPE = "App\\Models\\User"

/**
 * @param {Record<string, unknown>} student
 * @param {boolean} [selectOnlyNames]
 * @returns {string}
 */
export function formatStudentSelectLabel(student, selectOnlyNames = false) {
  const first = String(student?.first_name ?? "").trim()
  const last = String(student?.last_name ?? "").trim()
  const name = `${first} ${last}`.trim()
  const email = String(student?.email ?? "").trim()
  const id = student?.id ?? ""

  if (selectOnlyNames) {
    return name || email || `Student ID: ${id}`
  }

  return email ? `${name}, ${email}` : name || `Student ID: ${id}`
}

/**
 * Maps a `/search` student row to a LazyLoadSelect-compatible option.
 * @param {Record<string, unknown>} student
 * @param {boolean} [selectOnlyNames]
 * @returns {{ label: string, value: { id: unknown, type: string } }}
 */
export function mapSearchStudentToOption(student, selectOnlyNames = false) {
  return {
    value: {
      id: student?.id,
      type: USER_TYPE,
      passesToday: student?.passes_for_today_count,
      unavailability: Object.prototype.hasOwnProperty.call(
        student,
        "active_unavailability"
      )
        ? student.active_unavailability
        : null
    },
    label: formatStudentSelectLabel(student, selectOnlyNames)
  }
}

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   searchQuery?: string,
 *   activeOnly?: boolean
 * }} [args]
 * @returns {Record<string, string | number | boolean>}
 */
export function buildStudentSearchParams({
  page = 1,
  perPage = 25,
  searchQuery = "",
  activeOnly = true
} = {}) {
  const params = {
    per_page: perPage,
    page,
    type: "student",
    search_query: "",
    active_only: activeOnly
  }

  const q = String(searchQuery ?? "").trim()
  if (q) {
    params.search_query = `name:"${q}", email:"${q}"`
    params.operator = "OR"
  }

  return params
}

/**
 * @param {unknown} modelValue
 * @returns {Array<Record<string, unknown>>}
 */
export function getSelectedStudentOptions(modelValue) {
  if (modelValue == null || modelValue === "") {
    return []
  }

  return Array.isArray(modelValue) ? modelValue.filter(Boolean) : [modelValue]
}

/**
 * Keeps selected students visible even when they are not on the loaded page.
 * @param {Array<Record<string, unknown>>} selectedOptions
 * @param {Array<Record<string, unknown>>} loadedOptions
 * @returns {Array<Record<string, unknown>>}
 */
export function mergeStudentSelectOptions(selectedOptions, loadedOptions) {
  const merged = [...(loadedOptions || [])]
  const seenIds = new Set(
    merged.map((option) => String(option?.value?.id ?? "")).filter(Boolean)
  )

  ;(selectedOptions || []).forEach((option) => {
    const id = String(option?.value?.id ?? "")
    if (!id || seenIds.has(id)) {
      return
    }
    merged.unshift(option)
    seenIds.add(id)
  })

  return merged
}

/**
 * @param {unknown} response
 * @returns {{ options: Array<Record<string, unknown>>, lastPage: number | null }}
 */
export function parseStudentSearchResponse(response, selectOnlyNames = false) {
  const data = response?.data?.data
  const meta = response?.data?.meta
  const rows = Array.isArray(data) ? data : []
  const options = rows
    .filter((student) => student?.role_id)
    .map((student) => mapSearchStudentToOption(student, selectOnlyNames))

  return {
    options,
    lastPage: meta?.last_page != null ? Number(meta.last_page) : null
  }
}
